<template>
    <div ref="root" class="team-jersey-badge" :style="{ width: size + 'px', height: size + 'px' }">
        <Avatar3DPicker
            v-if="render_3d"
            :key="jersey_model_url"
            :model-url="jersey_model_url"
            :color-options="[{ label: '', hex: color }]"
            :model-value="color"
            :interactive="false"
            :size="size"
        />
        <div v-else class="team-jersey-badge-dot" :style="{ backgroundColor: color, width: dot_size + 'px', height: dot_size + 'px' }"></div>
    </div>
</template>

<script>
import { reactive } from 'vue'
import Avatar3DPicker from './Avatar3DPicker.vue'
import { get_active_jersey_model_url } from '@/utils/jersey_model'

// Browsers cap total live WebGL contexts per page (Chrome: ~16) - a team
// list can easily have more entries than that. active_ids is a reactive Set
// shared by every instance of this component on the page: each badge's
// render_3d computed depends on it, so freeing a slot (an instance scrolling
// out of view or unmounting) automatically makes every other waiting,
// currently-visible badge re-check whether it can claim it - no manual
// polling or pub/sub needed, just plain Vue reactivity.
const MAX_CONCURRENT_RENDERS = 8;
const active_ids = reactive(new Set());
let next_badge_id = 0;

// Generic "show this team's jersey, live 3D if there's room for it,
// otherwise a flat color dot" widget - knows nothing about rankings/panels/
// whatever list it's used in, just a color and a size.
export default {
    name: 'TeamJerseyBadge',
    components: {
        Avatar3DPicker,
    },
    props: {
        color: {
            type: String,
            required: true,
        },
        size: {
            type: Number,
            default: 64,
        }
    },
    data(){
        return {
            badge_id: next_badge_id++,
            in_view: false,
        };
    },
    computed: {
        // This event's own uploaded 3D model, or the app-wide default if it
        // hasn't configured one - see @/utils/jersey_model.js.
        jersey_model_url(){
            return get_active_jersey_model_url();
        },
        dot_size(){
            return Math.round(this.size * 0.4);
        },
        render_3d(){
            if(!this.in_view)
                return false;
            if(active_ids.has(this.badge_id))
                return true;
            return active_ids.size < MAX_CONCURRENT_RENDERS;
        }
    },
    watch: {
        render_3d(should_render){
            if(should_render)
                active_ids.add(this.badge_id);
            else
                active_ids.delete(this.badge_id);
        }
    },
    mounted(){
        this._observer = new IntersectionObserver((entries) => {
            this.in_view = !!(entries[0] && entries[0].isIntersecting);
        }, { threshold: 0.2 });
        this._observer.observe(this.$refs.root);
    },
    beforeUnmount(){
        if(this._observer)
            this._observer.disconnect();
        active_ids.delete(this.badge_id);
    }
}
</script>

<style scoped>
    .team-jersey-badge{
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .team-jersey-badge-dot{
        border-radius: 50%;
        border: 1px solid rgba(0, 0, 0, 0.15);
    }
</style>
